import { Modal, PrimaryButton, RadioGroup, RadioGroupDataInterface, toast } from '@systemeio/ui'
import { BadRequest, ErrorResponseContentInterface } from '@systemeio/util'
import FormSenderEmailInput from 'modules/email/components/form-sender-email-input'
import { useSendTestEmail } from 'modules/mailing/hooks/use-send-test-email'
import { useMailingSettings } from 'modules/profile/mailing-settings/hooks/use-mailing-settings'
import { MailingSettingsInterface } from 'modules/profile/mailing-settings/types/mailing-settings-interface'
import React, { useEffect, useState } from 'react'
import { useMailingState } from 'shared/components/dashboard-mail-editor/hooks/use-mailing-state'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { HTMLString } from 'shared/types/html-string'

interface SaveAndTestSplitTestProps {
  opened: boolean
  onClose: () => void
  onSave: () => Promise<void>
  variantA: ReturnType<typeof useMailingState>
  variantB: ReturnType<typeof useMailingState>
}

export function SaveAndTestSplitTest({
  onClose,
  opened,
  onSave,
  variantA,
  variantB,
}: SaveAndTestSplitTestProps) {
  const { t } = useLocoTranslation()
  const [email, setEmail] = useState<MailingSettingsInterface['testEmailAddress'] | null>(null)
  const [hasEmailBeenChanged, setHasEmailBeenChanged] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [mailingId, setMailingId] = useState<number>()

  const [isFetching, setIsFetching] = useState(false)

  const { fetcherSendTestEmail } = useSendTestEmail()
  const { mailingData } = useMailingSettings()

  const variantsData: RadioGroupDataInterface<number>[] = [
    { id: variantA.mailing.id, caption: t('dashboard.newsletter_editor.split_test.variant_a') },
    { id: variantB.mailing.id, caption: t('dashboard.newsletter_editor.split_test.variant_b') },
  ]

  const handelClose = () => {
    onClose()
  }

  const afterLeave = () => {
    setError(null)
    setMailingId(undefined)
    setEmail(null)
    setHasEmailBeenChanged(false)
  }

  const handleConfirm = async () => {
    if (!email || !mailingId) return
    setIsFetching(true)
    try {
      await onSave()
      await fetcherSendTestEmail(mailingId, {
        email,
      })
      onClose()
    } catch (e) {
      if (e instanceof BadRequest) {
        const errors = e.errors as ErrorResponseContentInterface<{ email: HTMLString[] }>
        if (errors?.fields?.email) {
          setError(errors.fields.email.join(''))
        }
        if (errors.common?.length) {
          toast.error(
            <p
              className={'[&>a]:primary-link'}
              dangerouslySetInnerHTML={{ __html: errors.common.join('') }}
            />,
          )
        }
      }
    }
    setIsFetching(false)
  }

  useEffect(() => {
    if (!hasEmailBeenChanged && !email && mailingData) {
      setEmail(mailingData.testEmailAddress)
    }
  }, [email, hasEmailBeenChanged, mailingData, mailingData?.testEmailAddress])

  return (
    <Modal
      afterLeave={afterLeave}
      isFetching={isFetching}
      isCentered
      opened={opened}
      onClose={handelClose}
      title={t('dashboard.newsletter_editor.send_test_modal_title')}
    >
      <form className="m-auto flex w-full flex-col gap-6 lg:gap-10">
        <div className={'flex flex-col gap-5'}>
          <FormSenderEmailInput
            value={email || ''}
            className="w-full"
            key={'email'}
            isPreFetching={!mailingData}
            onChange={e => {
              setHasEmailBeenChanged(true)
              setEmail(e.target.value)
            }}
            htmlFor={'none'}
            label={t('dashboard.email.test_email_address')}
            required
            error={error}
          />
          <div className={'flex justify-center'}>
            <RadioGroup
              className={'items-center'}
              label={t('dashboard.newsletter_editor.split_test.save_and_test.selected_variant')}
              options={variantsData}
              selected={mailingId}
              onChange={setMailingId}
            />
          </div>
        </div>
        <div className="flex justify-center">
          <PrimaryButton
            isFetching={isFetching}
            disabled={!mailingData || !email || !mailingId}
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              handleConfirm()
            }}
            type="submit"
          >
            {t('global.confirm')}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  )
}
